import * as React from "react";

import qs from "qs";

import {
  AsyncTypeahead,
  KeyValue,
  MetaSegment,
  PageEnvelope,
  useAsyncTypeahead,
  WorldLocation,
} from "@skv/core";

import { useHistory, useLocation } from "react-router-dom";

import {
  organisationResultStore,
  OrganisationSearchQueryState,
  usePageEnvelope,
  useThunkDispatch,
} from "@skv/state";

interface Organisation {
  category: MetaSegment<number>;
  friendlyName: string;
  id: number;
  location: WorldLocation;
  name: string;
  url: string;
}

interface OrganisationSearchPageEnvelopeData {
  organisationFilters: {
    categoryList: KeyValue[];
  };
  organisationResult: {
    canonicalUrl: string;
    organisations: Organisation[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  searchCriteria: {
    // todo : category
    category: Array<any>;
    keyword: string;
    page: number | null;
    where: string | null;
  };
}

const OrganisationSearchForm: React.FC<Record<string, never>> = () => {
  const history = useHistory();
  const location = useLocation<{
    searchQuery?: OrganisationSearchQueryState;
  }>();

  const { pageEnvelope } =
    usePageEnvelope<OrganisationSearchPageEnvelopeData>();

  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  const dispatch = useThunkDispatch();

  const initialSearchQuery = React.useMemo(() => {
    return Object.assign(
      {},
      pageEnvelope.data?.searchCriteria,
      location.state?.searchQuery
    );
  }, []);

  const [keyword, setKeyword] = React.useState(
    initialSearchQuery.keyword || (query.keyword as string) || ""
  );

  const [where, setWhere] = React.useState(initialSearchQuery.where || "");
  const [whereSelected, setWhereSelected] = React.useState(
    initialSearchQuery.where ? [initialSearchQuery.where] : []
  );

  const didWhereAutoComplete = !!whereSelected.length;

  const locationTypeahead = useAsyncTypeahead<
    string,
    PageEnvelope<{
      locations: string[];
    }>
  >({
    url: (query) => `/api/locations?q=${query}`,
    onSuccess: (res) => {
      return res.data.locations;
    },
    defaultOptions: initialSearchQuery.where ? [initialSearchQuery.where] : [],
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const searchQuery = {
      ...location.state?.searchQuery,
      keyword,
      where: whereSelected.length ? whereSelected[0] : where,
    };

    dispatch(
      organisationResultStore.actions.fetchOrganisations(searchQuery)
    ).then((data) => {
      history.push(data.canonicalUrl, {
        searchQuery,
        analytics: {
          didWhereAutoComplete,
        },
      });
    });
  };

  return (
    <section>
      <div className="nsw-container mt-n5">
        <div className="bg-secondary p-4">
          <form
            css={`
              .form-control {
                border-color: #fff;
              }
            `}
            method="GET"
            onSubmit={handleSubmit}
            action="/volunteering-organisations"
          >
            <div className="row align-items-end mx-n2 mt-lg-n3">
              <div
                className="col-md nsw-form__group d-flex flex-column px-2"
                css={`
                  overflow-x: clip;
                `}
              >
                <label className="nsw-form__label" htmlFor="keyword">
                  Keyword
                </label>
                <span className="sr-only">Name or keyword</span>
                <input
                  className="nsw-form__input focus-primary border-primary"
                  id="keyword"
                  name="keyword"
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Name or keyword"
                />
              </div>
              <div
                className="col-md nsw-form__group d-flex flex-column px-2"
                css={`
                  overflow-x: clip;
                `}
              >
                <label className="nsw-form__label" htmlFor="locations">
                  Location
                </label>
                <span className="sr-only">Enter suburb, town or city</span>
                <AsyncTypeahead
                  id="where"
                  inputProps={{
                    id: "where",
                    name: "where",
                    className: "nsw-form__input focus-primary border-primary",
                  }}
                  wrapperProps={{
                    className: "nsw-form__label",
                  }}
                  onInputChange={(text) => setWhere(text)}
                  onSearch={locationTypeahead.onSearch}
                  onChange={(selected) => setWhereSelected(selected)}
                  isLoading={locationTypeahead.isLoading}
                  options={locationTypeahead.options}
                  selected={whereSelected}
                  placeholder="Enter suburb, town or city"
                />
              </div>
              <div className="px-2 nsw-form__group flex-grow-1 flex-md-grow-0">
                <button
                  className="btn btn-block btn-primary"
                  aria-label="Search for opportunities"
                  css={`
                    min-width: 100px;
                  `}
                >
                  Find
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default OrganisationSearchForm;
